import { Shield, Award, Clock, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[400px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1562036861-44811e0b4481?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxiYWhhbWFzJTIwYmVhY2h8ZW58MHx8fHwxNzc0Mzk4ODIzfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">About RELAX & RIDE</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Your trusted transportation partner in The Bahamas
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Our Story</h2>
              <div className="w-20 h-1 bg-red-600 mx-auto mb-6"></div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-6 text-lg leading-relaxed">
                Welcome to RELAX & RIDE, Nassau's premier taxi service dedicated to providing exceptional 
                transportation experiences throughout The Bahamas. Founded with a passion for hospitality 
                and a commitment to excellence, we have become the trusted choice for both tourists and 
                locals seeking reliable, comfortable, and professional taxi services.
              </p>
              <p className="mb-6 text-lg leading-relaxed">
                Our mission is simple: to make every journey memorable. Whether you're arriving at Nassau 
                International Airport for the first time, exploring the stunning beaches of Paradise Island, 
                or attending an important business meeting, we ensure you travel in comfort and style.
              </p>
              <p className="text-lg leading-relaxed">
                What sets us apart is our unwavering commitment to customer satisfaction. Our drivers are 
                not just professionals behind the wheel – they're local experts who love sharing the beauty 
                and culture of The Bahamas with every passenger. We combine punctuality, safety, and friendly 
                service to create transportation experiences that exceed expectations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center hover:shadow-xl transition-shadow border-t-4 border-red-600">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Safety First</h3>
                <p className="text-gray-600">
                  Your safety is our top priority. All our drivers are licensed, insured, and trained professionals.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-t-4 border-red-600">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Excellence</h3>
                <p className="text-gray-600">
                  We strive for excellence in every aspect of our service, from booking to drop-off.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-t-4 border-red-600">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Reliability</h3>
                <p className="text-gray-600">
                  Count on us to be on time, every time. We monitor flights and traffic to ensure punctuality.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-t-4 border-red-600">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Customer Focus</h3>
                <p className="text-gray-600">
                  Your satisfaction drives us. We go the extra mile to make your journey exceptional.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Why Choose Us?</h2>
              <div className="w-20 h-1 bg-red-600 mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Local Expertise</h3>
                  <p className="text-gray-600">
                    Our drivers are born and raised in The Bahamas, with intimate knowledge of every road, 
                    attraction, and hidden gem on the islands.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">24/7 Availability</h3>
                  <p className="text-gray-600">
                    Day or night, weekday or weekend, we're always ready to serve you. Late flight? 
                    Early departure? We've got you covered.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Comfortable Vehicles</h3>
                  <p className="text-gray-600">
                    Our well-maintained vehicles are clean, air-conditioned, and designed for your comfort 
                    throughout your journey.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Transparent Pricing</h3>
                  <p className="text-gray-600">
                    No hidden fees or surprises. We provide clear, upfront pricing so you know exactly 
                    what to expect.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  5
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Personalized Service</h3>
                  <p className="text-gray-600">
                    Every customer is unique. We tailor our services to meet your specific needs and 
                    preferences, ensuring a personalized experience.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  6
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Proven Track Record</h3>
                  <p className="text-gray-600">
                    With countless satisfied customers and five-star reviews, we've earned our reputation 
                    as Nassau's most trusted taxi service.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Experience the Difference</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust RELAX & RIDE for their transportation needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/booking" className="inline-block">
              <button className="bg-white text-red-600 hover:bg-gray-100 px-10 py-4 rounded-md font-semibold text-lg transition-colors">
                Book Now
              </button>
            </a>
            <a href="/contact" className="inline-block">
              <button className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-10 py-4 rounded-md font-semibold text-lg transition-colors">
                Contact Us
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
